'use client'

import { create } from 'zustand'
import type { KnowledgePortalSurface } from '@/lib/knowledge/portal-types'

/** What an entry point asks for when it opens the assistant. */
export interface AskOpenRequest {
  /** A question to send as the first message of a new conversation. */
  question?: string
  /** The surface to ask on; the drawer's default when left out. */
  surface?: KnowledgePortalSurface
  /** Where focus goes back to when the drawer closes; the focused element when left out. */
  opener?: HTMLElement | null
}

/** A question waiting for the drawer to send it (the drawer clears it once sent). */
export interface PendingQuestion {
  id: number
  question: string
  surface: KnowledgePortalSurface | null
}

/**
 * The portal assistant's shared state.
 *
 * The drawer and the floating robot live in the portal layout
 * (`KnowledgeAskLauncher`), while the ways into it live in the pages: the
 * "Ask AI" button in each portal header, the suggestion cards, the inline
 * "Ask the assistant" box on the library page. They meet here:
 *
 *   - `available`: whether an assistant is available to this person now. The
 *     launcher, which reads GET /assistant, publishes it, so a header button
 *     never fetches anything of its own (the dashboard renders without one).
 *   - `open` / `openAssistant` / `closeAssistant`: the drawer, and the element
 *     focus returns to when it closes.
 *   - `pending`: a question asked outside the drawer. The drawer opens, starts a
 *     new conversation and sends it as the first message.
 *   - `managerView`: the dashboard's manager view toggle; the drawer offers a
 *     manager the "Policies / Team & manager" tabs while it is on. In memory
 *     only: the dashboard's own toggle starts off on every load, and so does this.
 */
interface KnowledgeAskState {
  managerView: boolean
  setManagerView: (on: boolean) => void
  available: boolean
  setAvailable: (available: boolean) => void
  open: boolean
  opener: HTMLElement | null
  pending: PendingQuestion | null
  openAssistant: (request?: AskOpenRequest) => void
  closeAssistant: () => void
  clearPending: (id: number) => void
}

let nextPending = 0

function focusedElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  return document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null
}

export const useKnowledgeAskStore = create<KnowledgeAskState>()((set) => ({
  managerView: false,
  setManagerView: (on) => set({ managerView: on }),
  available: false,
  setAvailable: (available) => set({ available }),
  open: false,
  opener: null,
  pending: null,
  openAssistant: (request = {}) => {
    const question = request.question?.trim() ?? ''
    set((state) => ({
      open: true,
      // An opener already recorded (the drawer is open) keeps its place.
      opener: state.open ? state.opener : (request.opener ?? focusedElement()),
      pending: question ? { id: (nextPending += 1), question, surface: request.surface ?? null } : state.pending,
    }))
  },
  closeAssistant: () => set({ open: false }),
  clearPending: (id) => set((state) => (state.pending?.id === id ? { pending: null } : {})),
}))
